<script setup>
import {
  computed,
  inject,
  onBeforeMount,
  onMounted,
  onUnmounted,
  provide,
  watch,
} from "vue";
import AppKintzNavBar from "./components/layout/kintz-app/AppKintzNavBar.vue";
import { kintzAppColors } from "./composable/kintz/kintz-app.color";
import { ref } from "vue";
import eventBus from "./plugins/eventBus";
import { useAuth } from "@websanova/vue-auth";
import { usePusherStore } from "./stores/pusher";
import { useRoute } from "vue-router";
import { isMobile } from "vue-device-detect";
import Echo from "laravel-echo";
import NotificationDialog from "./components/partials/NotificationDialog.vue";
import NotificationComponent from "./components/partials/NotificationComponent.vue";
import AppKintzFooter from "./components/layout/kintz-app/AppKintzFooter.vue";
import AppKintzBreadcrumbs from "./components/layout/kintz-app/AppKintzBreadcrumbs.vue";
import AppKintzBanner from "./components/layout/kintz-app/AppKintzBanner.vue";
import AppKintzHomeCarousel from "./components/layout/kintz-app/AppKintzHomeCarousel.vue";
import GlobalInputPhoneNumberDialog from "./components/layout/GlobalInputPhoneNumberDialog.vue";
import { useAuthStore } from "./stores";
import { storeToRefs } from "pinia";
import { nextTick } from "vue";
import { initialize_jam_code_snippet } from "./plugins/jam";

window.Pusher = require("pusher-js");

let intervalId = null;

const authStore = useAuthStore();
const { kintzApp } = storeToRefs(authStore);
const auth = useAuth();
const pusherConfig = usePusherStore();
const axios = inject("axios");
const pusherKey = ref("");
const banner = ref(false);
const showSavePhoneNumber = ref(false);
const appleStore = ref("");
const route = useRoute();

const notification = ref({
  message: "",
  show: false,
  icon: true,
});

const enabledBanner = computed(() => authStore.isEnabledBanner);
const showMobilePhoneDialog = computed(() =>
  localStorage.getItem("showPhoneNumberDialog")
);
const isImpersonating = localStorage.getItem("impersonate_user") ? true : false;

const handleAddBodyClass = () => {
  document.body.classList.add("bg-kintz-primary-1");
  document.documentElement.style.setProperty(
    "--el-color-primary",
    kintzAppColors.primary_2
  );
  document.documentElement.style.setProperty(
    "--el-button--primary",
    kintzAppColors.primary_2
  );
};

const showBannerForMobile = () => {
  const hasViewBanner = localStorage.getItem("has_view_banner");

  if (isMobile && !hasViewBanner && !window.MSStream) {
    banner.value = true;
  }
};

// const handleCloseBanner = () => {
//   localStorage.setItem("has_view_banner", 1);
//   banner.value = false;
// };

const setEnv = async () => {
  await axios.get("/api/get-env").then(({ data }) => {
    pusherConfig.setPusher(data);
    pusherKey.value = {
      pusher_cluster: data.pusher_cluster,
      pusher_key: data.pusher_key,
    };
    appleStore.value = data.apple_store;
  });
};

const goBack = () => {
  axios
    .get("/api/okta/get-logout-link", {
      params: {
        user_central_id: route.query.user_central_id,
      },
    })
    .then(({ data }) => {
      window.location.href = data;
    });
};

const logout = () => {
  axios.get("/api/okta/logout").then(({ data }) => {
    auth.logout();
    localStorage.removeItem("nextUrl");
    window.location.href = data;
  });
};

const ssoLogoutListener = () => {
  window.Echo.channel("sso-logout").listen("SSOLogout", (e) => {
    let centralId = auth.user()
      ? auth.user().central_id
      : route.query.user_central_id;

    if (centralId != e.user_central_id) {
      return;
    }

    if (route.query.user_central_id) {
      goBack();
      return;
    }

    logout();
  });
};


/**
 * Jam Recorder
 * Initialize Code Snippet
 * Initialize Jam Recorder Bug Button
 * Make sure the dom is loaded
 * @return {void}
 */
const initialize_jam_recorder = async () => {
  await nextTick();
  // initialize jam code snippet
  initialize_jam_code_snippet().then(() => {
    // add bug button
    const bugBtn = document.createElement("div");
    bugBtn.innerHTML = "🐞";
    Object.assign(bugBtn.style, {
      position: "fixed",
      bottom: "17px",
      right: "140px",
      width: "40px",
      height: "40px",
      backgroundColor: "#e74c3c",
      color: "white",
      fontSize: "24px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
      zIndex: "9999",
    });
    bugBtn.title = "Report a Bug";
    bugBtn.onclick = () => {
      window.open("https://recorder.jam.dev/i31aeVd", "_blank");
    };
    document.body.appendChild(bugBtn);
  });
};

const setupPusher = () => {
  window.Echo = new Echo({
    broadcaster: "pusher",
    key: pusherKey.value.pusher_key,
    cluster: pusherKey.value.pusher_cluster,
    forceTLS: true,
    authEndpoint: "/broadcasting/auth",
    encrypted: true,
    auth: {
      headers: {
        Authorization: `Bearer ${auth.token()}`,
      },
    },
  });

  ssoLogoutListener();
};

const startAuthRefresh = async () => {
  if (intervalId) clearInterval(intervalId);

  await checkAuthUser();

  intervalId = setInterval(() => {
    checkAuthUser();
  }, 5 * 60 * 1000); // every 5 minutes
};

const checkAuthUser = async () => {
  const user = auth.user();
  showSavePhoneNumber.value = !user?.phone_number && !isImpersonating;
};

onBeforeMount(() => {
  kintzApp.value = true;
});

onMounted(async () => {
  handleAddBodyClass();
  showBannerForMobile();
  await setEnv();
  setupPusher();

  if (authStore.user?.id) {
    startAuthRefresh();
  }

  eventBus.$on("NOTIFICATION_DIALOG", (data) => {
    notification.value = {
      show: true,
      message: data.message,
      icon: data.icon,
    };
  });
  initialize_jam_recorder();
});

onUnmounted(() => {
  document.body.classList.remove("bg-kintz-primary-1");

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
});

provide("kintz-app", true);

watch(
  () => authStore.user,
  (newVal, oldVal) => {
    if (newVal?.id && !oldVal?.id) {
      startAuthRefresh();
    } else if (!newVal?.id && oldVal?.id) {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;
    }
  }
);
</script>
<template>
  <div
    class="min-h-screen bg-fixed flex flex-col bg-kintz-primary-4 bg-[url(https://kintz-lms.s3.us-east-1.amazonaws.com/vt__bg_00-DF3C36B7-976C-DDB7--FC753F0A-D02E-7B5B-769000D7D57236AC.jpg)] bg-cover bg-center transition-all duration-1000"
  >
    <div class="min-h-full flex flex-col">
      <AppKintzBanner v-if="$auth.check()" />
      <AppKintzNavBar v-if="$auth.check()" />

      <div class="flex-1 flex flex-col">
        <AppKintzBreadcrumbs v-if="$auth.check()" />
        <AppKintzHomeCarousel v-if="route.name === 'home' && enabledBanner" />
        <main class="flex-1 flex-col w-full mx-auto max-w-[2000px] flex">
          <router-view />
        </main>
      </div>
    </div>
    <NotificationDialog
      v-if="notification.show"
      :content="notification.message"
      :icon="notification.icon"
      @close="notification.show = false"
    ></NotificationDialog>
    <!-- <DownloadBanner
      v-model="banner"
      :handleCloseBanner="handleCloseBanner"
      :link="appleStore"
    /> -->
    <NotificationComponent />
    <GlobalInputPhoneNumberDialog
      v-if="
        ($auth.check() && !showMobilePhoneDialog) ||
        ($auth.check() && showSavePhoneNumber)
      "
    />
    <AppKintzFooter v-if="$auth.check()" />
    <div id="modal_dialog_teleport"></div>
  </div>
</template>

<style lang="css">
:root {
  --el-color-primary: #161616 !important;
}
</style>
